import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, mergeMap, startWith, switchMap } from 'rxjs/operators';

import {
  loadOrganicMatterBalance,
  loadOrganicMatterBalanceFailed,
  loadOrganicMatterBalanceSuccess,
  loadOrganicMatterBenchmark,
  loadOrganicMatterBenchmarkFailed,
  loadOrganicMatterBenchmarkSuccess,
} from './organic-matter-balance.actions';
import { cropYearChanged } from '../../../../store/actions';
import { OrganicMatterBalanceState } from '../organic-matter-balance.models';
import { OrganicMatterService } from '../services/organic-matter.service';

@Injectable()
export class OrganicMatterBalanceEffects {
  private readonly actions$ = inject(Actions);
  private readonly organicMatterService = inject(OrganicMatterService);
  private readonly store = inject<Store<OrganicMatterBalanceState>>(Store);

  cropYearChanged$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cropYearChanged),
      mergeMap(({ cropYear }) => [loadOrganicMatterBalance({ cropYear }), loadOrganicMatterBenchmark({ cropYear })]),
    ),
  );

  loadOrganicMatterBalance$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOrganicMatterBalance),
      switchMap(({ payload: { cropYear } }) =>
        this.organicMatterService.fetchOrganicMatterBalanceReport(cropYear).pipe(
          mergeMap((report) => [loadOrganicMatterBalanceSuccess({ report })]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
      catchError(() => of(loadOrganicMatterBalanceFailed({ message: 'errors.loadOrganicMatterBalanceFailed' }))),
    ),
  );

  loadOrganicMatterBenchmark$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOrganicMatterBenchmark),
      switchMap(({ payload: { cropYear } }) =>
        this.organicMatterService.fetchOrganicMatterBenchmark(cropYear).pipe(
            mergeMap((benchmark) => [loadOrganicMatterBenchmarkSuccess({ benchmark })]),
          catchError((error: HttpErrorResponse) => (error.status === 404 ? EMPTY : throwError(() => error))),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
      catchError(() => of(loadOrganicMatterBenchmarkFailed({ message: 'errors.loadOrganicMatterBalanceFailed' }))),
    ),
  );
}
